import { Box, Button, Center, Code, Loader, Stack, Text, ThemeIcon } from '@mantine/core';
import { IconAlertTriangle, IconCheck, IconLock } from '@tabler/icons-react';
import { motion } from 'framer-motion';

import { QrAuthState } from '../../../../../types';

import { QR_CARD_SIZE } from './constants';
import { ScanInstructions, ScanState } from './ScanState';

export function QrAuthBody({ qrAuth, pageId }: { qrAuth: QrAuthState | null; pageId: string }) {
  if (!qrAuth) {
    return <PendingState label="Starting the Telegram client…" />;
  }

  if (qrAuth.status === 'pending') {
    return <PendingState label="Requesting a QR code…" />;
  }

  if (qrAuth.status === 'passwordRequired') {
    return <PasswordRequiredState pageId={pageId} />;
  }

  if (qrAuth.status === 'ready') {
    return <SuccessState />;
  }

  if (!qrAuth.link) {
    return <PendingState label="Waiting for the link…" />;
  }

  return <ScanState link={qrAuth.link} updatedAt={qrAuth.updatedAt} />;
}

function PasswordRequiredState({ pageId }: { pageId: string }) {
  return (
    <Stack align="center" gap="md" py="lg">
      <ThemeIcon size={64} radius="xl" variant="light" color="yellow">
        <IconLock size={32} />
      </ThemeIcon>
      <Text fw={600}>QR code accepted</Text>
      <Text size="sm" c="dimmed" ta="center">
        This account has two-step verification enabled. Send the cloud password to the bot:
      </Text>
      <Code block>{`/code\n${pageId}\n0.0.0.0.0\n<cloud password>`}</Code>
      <Text size="xs" c="dimmed" ta="center">
        The digits are a placeholder — a QR login has no code step, only the password is used.
      </Text>
    </Stack>
  );
}

function PendingState({ label }: { label: string }) {
  return (
    <Stack align="center" gap="md">
      <Box
        w={QR_CARD_SIZE}
        h={QR_CARD_SIZE}
        style={{
          borderRadius: 12,
          display: 'grid',
          placeItems: 'center',
          background: 'var(--mantine-color-dark-6)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <motion.div
          animate={{ x: ['-120%', '120%'] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(100deg, transparent 20%, rgba(255,255,255,0.06) 50%, transparent 80%)',
          }}
        />
        <Loader size="md" />
      </Box>

      <Text size="sm" c="dimmed">
        {label}
      </Text>

      <ScanInstructions />
    </Stack>
  );
}

function SuccessState() {
  return (
    <Center mih={300}>
      <Stack align="center" gap="md">
        <Box style={{ position: 'relative', display: 'grid', placeItems: 'center' }}>
          <motion.div
            initial={{ scale: 0.6, opacity: 0.5 }}
            animate={{ scale: 1.9, opacity: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              width: 72,
              height: 72,
              borderRadius: '50%',
              border: '2px solid var(--mantine-color-teal-5)',
            }}
          />
          <motion.div
            initial={{ scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 18 }}
          >
            <ThemeIcon size={72} radius="xl" color="teal">
              <IconCheck size={40} />
            </ThemeIcon>
          </motion.div>
        </Box>
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.18 }}
        >
          <Text fw={600}>Signed in</Text>
        </motion.div>
      </Stack>
    </Center>
  );
}

export function FailureState({ message, onClose }: { message: string; onClose: () => void }) {
  return (
    <Center mih={300}>
      <Stack align="center" gap="md">
        <ThemeIcon size={64} radius="xl" variant="light" color="red">
          <IconAlertTriangle size={32} />
        </ThemeIcon>
        <Text size="sm" c="dimmed" ta="center">
          {message}
        </Text>
        <Button variant="light" onClick={onClose}>
          Close
        </Button>
      </Stack>
    </Center>
  );
}
